import React, { useRef, useEffect, useState } from 'react';
import { WrappedCard } from '../WrappedCard';

interface OBWIVideoCardProps {
  username?: string;
}

export const OBWIVideoCard: React.FC<OBWIVideoCardProps> = ({ username }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay
  const lastToggleTimeRef = useRef<number>(0);

  useEffect(() => {
    // Ensure video loads and plays immediately
    if (videoRef.current) {
      const video = videoRef.current;
      // Set attributes for better mobile support
      video.setAttribute('playsinline', 'true');
      video.setAttribute('webkit-playsinline', 'true');
      video.muted = true; // Must be muted for autoplay on mobile
      
      // Preload the video
      video.load();
      
      // Try to play immediately
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log('Autoplay prevented:', error);
          // On mobile, user interaction may be required
          // Video will be ready to play when user interacts
        });
      }
      
      // Handle when video becomes visible/enters viewport
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && video.paused) {
            video.play().catch(() => {
              // Play failed, will need user interaction
            });
          }
        });
      }, { threshold: 0.5 });
      
      observer.observe(video);
      
      return () => {
        observer.disconnect();
      };
    }
  }, []);

  const toggleMute = (e: React.MouseEvent | React.TouchEvent) => {
    console.log('[DIAG] OBWIVideoCard toggleMute fired, event type:', e.type);
    
    // Prevent double-triggering within 300ms
    const now = Date.now();
    if (now - lastToggleTimeRef.current < 300) {
      console.log('[DIAG] Toggle muted - ignoring duplicate trigger');
      e.stopPropagation();
      e.preventDefault();
      return;
    }
    lastToggleTimeRef.current = now;
    
    e.stopPropagation();
    e.preventDefault();
    if (videoRef.current) {
      const video = videoRef.current;
      const newMutedState = !video.muted;
      video.muted = newMutedState;
      setIsMuted(newMutedState);
      
      // On mobile, after user interaction, we can unmute
      // Ensure video continues playing after unmuting
      if (video.paused) {
        video.play().catch(() => {});
      } else {
        // If already playing, try to play again to ensure unmuted audio works
        // This helps on mobile where unmuting might need a play() call
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // If play fails, that's okay - video might already be playing
          });
        }
      }
    }
  };

  return (
    <WrappedCard className="relative" username={username}>
      <div className="space-y-3 md:space-y-4 relative z-10">
        {/* OBWI Video - prominent like Spotify wrapped with Instagram-style overlay */}
        <div className="flex justify-center mb-3 md:mb-4 relative z-50" style={{ pointerEvents: 'auto' }}>
          <div className="relative" style={{ width: 'fit-content', maxWidth: '100%' }}>
            <div className="absolute inset-0 bg-yellow-400 blur-3xl opacity-40 animate-pulse rounded-2xl"></div>
            <div className="relative rounded-2xl border-4 border-yellow-400 shadow-2xl overflow-hidden" style={{ maxHeight: '400px' }}>
              <video
                ref={videoRef}
                src="/obwi_wrapped.MOV"
                autoPlay
                loop
                muted={isMuted}
                playsInline
                preload="auto"
                className="w-full h-auto"
                style={{ 
                  maxHeight: '400px',
                  maxWidth: '100%',
                  height: 'auto',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  pointerEvents: 'auto',
                  touchAction: 'manipulation',
                  zIndex: 30,
                  position: 'relative'
                }}
                onError={(e) => {
                  const target = e.target as HTMLVideoElement;
                  console.error('Video error:', target.error);
                  // MOV might not be supported on all devices
                  // Could add fallback MP4 here if available
                }}
                onTouchStart={(e) => {
                  console.log('[DIAG] OBWIVideoCard video onTouchStart fired');
                  e.stopPropagation();
                  // Try to play on touch if paused (mobile autoplay restriction)
                  if (videoRef.current) {
                    if (videoRef.current.paused) {
                      videoRef.current.play().catch(() => {});
                    }
                    // After user interaction, we can unmute if user wants
                    // This allows sound after user touches the video
                  }
                }}
                onClick={(e) => {
                  console.log('[DIAG] OBWIVideoCard video onClick fired');
                  e.stopPropagation();
                  // Try to play on click if paused
                  if (videoRef.current) {
                    if (videoRef.current.paused) {
                      videoRef.current.play().catch(() => {});
                    }
                    // After user interaction, we can unmute if user wants
                  }
                }}
                onTouchEnd={(e) => {
                  console.log('[DIAG] OBWIVideoCard video onTouchEnd fired');
                }}
              />
              {/* Sound toggle button */}
              <button
                onClick={toggleMute}
                onTouchStart={toggleMute}
                className="absolute top-3 right-3 bg-black/70 hover:bg-black/90 rounded-full p-2 transition-colors z-10"
                aria-label={isMuted ? "Unmute video" : "Mute video"}
                style={{ pointerEvents: 'auto', touchAction: 'manipulation', zIndex: 100 }}
              >
                {isMuted ? (
                  <svg className="w-5 h-5 text-yellow-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-yellow-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                )}
              </button>
              {/* Instagram-style overlay at bottom */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4 md:p-5" style={{ zIndex: 50 }}>
                <div className="text-center" style={{ zIndex: 50 }}>
                  <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-yellow-200 drop-shadow-2xl tracking-wide">
                    10+1 OBWI Enrollee
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WrappedCard>
  );
};

